import { motion, useReducedMotion } from 'motion/react';
import coinsSrc from './assets/buyback-coins.png';

// Operator-themable icon art. The Design Constructor writes a full CSS
// image value (`url("…")` / `none`) into the var; when unset, the bundled
// coins image renders exactly as before. Must be an inline style — the
// url() fallback embeds a Vite-resolved asset path unknown to Tailwind.
const iconImageStyle = {
  backgroundImage: `var(--co-BuybackNotification-iconImage, url("${coinsSrc}"))`,
} as const;

/**
 * Animated buyback-coins badge for the payout toast. The badge POPS in on
 * mount (scale + a small tilt) to grab the eye, then a gold "glint" sweeps
 * diagonally across the coins ONCE (screen-blended white streak, so it
 * brightens the gold like a real shine) — a tiny jackpot beat that nudges
 * the next pull. A single sweep, not a loop, so it reads as one clean shine.
 *
 * Honors `prefers-reduced-motion` — falls back to the static badge.
 */
export function BuybackCoins() {
  const reduce = useReducedMotion();

  if (reduce) {
    return (
      <span
        aria-hidden
        className="block size-13 shrink-0 select-none [border-radius:var(--co-BuybackNotification-iconRadius,16px)] bg-cover bg-center bg-no-repeat"
        style={iconImageStyle}
      />
    );
  }

  return (
    <motion.span
      aria-hidden
      className="relative block size-13 shrink-0 select-none overflow-hidden [border-radius:var(--co-BuybackNotification-iconRadius,16px)] bg-cover bg-center bg-no-repeat"
      style={iconImageStyle}
      initial={{ scale: 0.6, rotate: -8 }}
      animate={{ scale: [0.6, 1.12, 1], rotate: [-8, 4, 0] }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], times: [0, 0.6, 1] }}
    >
      {/* Gold glint — a diagonal highlight that sweeps across the coins,
          screen-blended so it brightens rather than whitens the gold. */}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.85) 50%, transparent 60%)',
          mixBlendMode: 'screen',
        }}
        initial={{ x: '-120%' }}
        animate={{ x: '120%' }}
        transition={{ duration: 0.9, ease: 'easeInOut', delay: 0.3 }}
      />
    </motion.span>
  );
}
